// client/src/components/NotificationCenter.jsx
// Bell icon with dropdown notification panel + browser push notifications.
// Connects to SSE endpoint for real-time location change alerts.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { SERVER_ROOT } from "../config/apiBase";
import { formatEasternTime, getEasternTimeZoneLabel } from "../utils/timezone";

const MAX_NOTIFICATIONS = 50;

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const dropdownRef = useRef(null);
  const sseRef = useRef(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Request browser notification permission on first interaction
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      setPermissionGranted(true);
      return;
    }
    if (Notification.permission !== "denied") {
      const result = await Notification.requestPermission();
      setPermissionGranted(result === "granted");
    }
  }, []);

  // Connect to SSE stream
  useEffect(() => {
    const connect = () => {
      const es = new EventSource(`${SERVER_ROOT}/webhooks/notifications/stream`);

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "connected") return; // ignore handshake

          const notification = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            ...data,
            read: false,
          };

          setNotifications((prev) => [notification, ...prev].slice(0, MAX_NOTIFICATIONS));

          // Browser push notification
          if ("Notification" in window && Notification.permission === "granted") {
            try {
              new Notification(data.title || "ShipGuard Alert", {
                body: data.message,
                icon: "/favicon.ico",
                tag: `shipguard-${notification.id}`,
              });
            } catch (_) { /* silent */ }
          }

          // Play alert sound for location changes
          if (data.type === "location_change") {
            playAlertSound();
          }
        } catch (_) { /* ignore parse errors */ }
      };

      es.onerror = () => {
        es.close();
        // Reconnect after 5s
        setTimeout(connect, 5000);
      };

      sseRef.current = es;
    };

    connect();
    return () => {
      if (sseRef.current) sseRef.current.close();
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const clearAll = () => {
    setNotifications([]);
    setOpen(false);
  };

  const toggleOpen = () => {
    setOpen((v) => !v);
    if (!permissionGranted) requestPermission();
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      {/* Bell Button */}
      <button
        onClick={toggleOpen}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: "6px 10px",
          borderRadius: 8,
          display: "flex",
          alignItems: "center",
          gap: 4,
          color: "#94a3b8",
          fontSize: 18,
          position: "relative",
          transition: "background 0.15s",
        }}
        title="Notifications"
      >
        <span role="img" aria-label="notifications">
          {unreadCount > 0 ? "\uD83D\uDD14" : "\uD83D\uDD15"}
        </span>
        {unreadCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: 2,
              right: 4,
              minWidth: 18,
              height: 18,
              borderRadius: 9,
              background: "#ef4444",
              color: "white",
              fontSize: 10,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 4px",
              boxShadow: "0 0 6px rgba(239,68,68,0.5)",
              animation: "pulse 2s ease-in-out infinite",
            }}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 8,
            width: 380,
            maxHeight: 480,
            background: "#1a1f2e",
            borderRadius: 14,
            boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(139,92,246,0.15)",
            overflow: "hidden",
            zIndex: 9999,
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontWeight: 800, fontSize: 14, color: "#e2e8f0" }}>
              Notifications
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              {unreadCount > 0 && (
                <button onClick={markAllRead} style={actionBtnStyle}>
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button onClick={clearAll} style={actionBtnStyle}>
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Permission banner */}
          {"Notification" in window && Notification.permission === "default" && (
            <div
              style={{
                padding: "10px 16px",
                background: "rgba(59,130,246,0.1)",
                borderBottom: "1px solid rgba(59,130,246,0.15)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 12, color: "#93c5fd", flex: 1 }}>
                Enable browser notifications for real-time alerts
              </span>
              <button
                onClick={requestPermission}
                style={{
                  padding: "4px 10px",
                  borderRadius: 6,
                  border: "none",
                  background: "#3b82f6",
                  color: "white",
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Enable
              </button>
            </div>
          )}

          {/* Notification list */}
          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: 13,
                }}
              >
                No notifications yet.
                <br />
                <span style={{ fontSize: 12 }}>
                  Location change alerts will appear here in real-time.
                </span>
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationItem key={n.id} notification={n} />
              ))
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
};

const NotificationItem = ({ notification }) => {
  const isLocationChange = notification.type === "location_change";
  const time = notification.timestamp ? new Date(notification.timestamp) : null;

  return (
    <div
      style={{
        padding: "12px 16px",
        borderBottom: "1px solid rgba(255,255,255,0.03)",
        background: notification.read
          ? "transparent"
          : "rgba(139,92,246,0.05)",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          flexShrink: 0,
          background: isLocationChange
            ? "rgba(239,68,68,0.15)"
            : "rgba(59,130,246,0.15)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
        }}
      >
        {isLocationChange ? "\uD83D\uDEA8" : "\uD83D\uDD14"}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: isLocationChange ? "#fca5a5" : "#e2e8f0",
            marginBottom: 2,
          }}
        >
          {notification.title}
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.4 }}>
          {notification.message}
        </div>
        {notification.data?.orderName && (
          <div
            style={{
              marginTop: 4,
              fontSize: 11,
              color: "#64748b",
              display: "flex",
              gap: 8,
            }}
          >
            {notification.data.newLocationName && (
              <span>To: {notification.data.newLocationName}</span>
            )}
          </div>
        )}
      </div>

      <div style={{ flexShrink: 0, fontSize: 11, color: "#64748b" }}>
        {time ? `${formatEasternTime(time)} ${getEasternTimeZoneLabel()}` : ""}
      </div>
    </div>
  );
};

const actionBtnStyle = {
  padding: "4px 10px",
  borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "transparent",
  color: "#94a3b8",
  fontSize: 11,
  fontWeight: 600,
  cursor: "pointer",
};

function playAlertSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = "sine";
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch (_) { /* silent fallback */ }
}

export default NotificationCenter;
